import { useCallback, useEffect, useState } from 'react';
import { money } from '../lib/money';
import { ApiFailure, api } from '../lib/api';

interface Period {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  statusLabel: string;
  orderCount: number;
  grossAmount: string;
  deduction: string;
  netAmount: string;
  currency: string;
  paidAt: string | null;
}

interface Item {
  id: string;
  type: string;
  amount: string;
  note: string | null;
  order: { orderNo: string; receiverName: string; deliveredAt: string | null } | null;
}

interface Detail extends Period {
  items: Item[];
}

const TYPE_LABEL: Record<string, string> = {
  DELIVERY: '配送费',
  BONUS: '奖励',
  PENALTY: '扣款',
  ADJUSTMENT: '调整',
};

/** 整数最小单位 → 人看的金额。全程整数,不碰浮点 —— 这是要打给人的钱 */

const day = (iso: string) => iso.slice(5, 10).replace('-', '/');

/**
 * 我的结算单。
 *
 * ★ 「今日收入」回答的是「我今天挣了多少」,
 *   结算单回答的是「这个月发我多少、什么时候发、为什么扣了我 30 块」。
 *   后者才是司机月底真正会盯着看的东西。
 *
 *   所以两条:
 *   1. **扣款必须写明原因**,而且和金额挨着显示。
 *      只看到「扣款 -30」没有原因,司机第一反应是被克扣了 ——
 *      解释成本比那 30 块高得多
 *   2. **明细列到每一单**。总额对不上时,他能自己找出是哪一单差了
 */
export function Settlements() {
  const [rows, setRows] = useState<Period[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api<Period[]>('/settlements?limit=50'));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (detail) {
    return <DetailView detail={detail} onBack={() => setDetail(null)} />;
  }

  return (
    // 外层(收入页)已经有 px-4,这里不再套一层
    <div>
      <p className="text-sm text-slate-500 mb-4">
        按周期结算。金额有疑问先看明细,再找调度。
      </p>

      {error && (
        <div className="mb-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && <p className="text-slate-400">加载中…</p>}

      {!loading && rows.length === 0 && !error && (
        <p className="text-slate-400 py-8 text-center">还没有结算单。</p>
      )}

      <div className="space-y-3">
        {rows.map((p) => (
          <button
            key={p.id}
            onClick={() => void openDetail(p.id, setDetail, setError)}
            className="w-full text-left rounded-2xl bg-white dark:bg-slate-800 p-4 border
                       border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-baseline justify-between">
              <span className="text-slate-900 dark:text-slate-100">
                {day(p.periodStart)} – {day(p.periodEnd)}
              </span>
              <StatusChip status={p.status} label={p.statusLabel} />
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-sm text-slate-500">{p.orderCount} 单</span>
              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {money(p.netAmount, p.currency)}
              </span>
            </div>
            {/* ★ 有扣款就在列表上直接标出来,别等点进去才发现 */}
            {p.deduction !== '0' && (
              <div className="mt-1 text-sm text-red-600 dark:text-red-400">
                含扣款 {money(p.deduction, p.currency)} · 点开看原因
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

async function openDetail(
  id: string,
  setDetail: (d: Detail) => void,
  setError: (e: string) => void,
): Promise<void> {
  try {
    setDetail(await api<Detail>(`/settlements/${id}`));
  } catch (e) {
    setError(e instanceof ApiFailure ? e.message : '打不开');
  }
}

function StatusChip({ status, label }: { status: string; label: string }) {
  const style =
    status === 'PAID'
      ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400'
      : status === 'CONFIRMED'
        ? 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400'
        : 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400';
  return <span className={`text-xs px-2 py-0.5 rounded-full ${style}`}>{label}</span>;
}

function DetailView({ detail, onBack }: { detail: Detail; onBack: () => void }) {
  const deliveries = detail.items.filter((i) => i.type === 'DELIVERY');
  const others = detail.items.filter((i) => i.type !== 'DELIVERY');

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b
                      border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} aria-label="返回" className="text-2xl text-slate-500">
          ‹
        </button>
        <div>
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {day(detail.periodStart)} – {day(detail.periodEnd)}
          </div>
          <StatusChip status={detail.status} label={detail.statusLabel} />
        </div>
      </div>

      <div className="px-4 pt-4">
        <div className="rounded-2xl bg-white dark:bg-slate-800 p-5 border
                        border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500">实付</div>
          <div
            aria-label="实付金额"
            className="mt-1 text-4xl font-bold text-slate-900 dark:text-slate-100 tabular-nums"
          >
            {money(detail.netAmount, detail.currency)}
          </div>
          {/* ★ 让人能一眼把账算平:配送费 − 扣款 = 实付 */}
          <div className="mt-3 space-y-1 text-sm">
            <Row label={`配送费(${deliveries.length} 单)`} value={money(detail.grossAmount, detail.currency)} />
            {detail.deduction !== '0' && (
              <Row
                label="扣款"
                value={`-${money(detail.deduction, detail.currency)}`}
                danger
              />
            )}
          </div>
        </div>

        {/* ★ 扣款和奖励放在最上面,而且必须带原因。
               只看到「扣款 -30」没有原因,司机第一反应是被克扣了 */}
        {others.length > 0 && (
          <section className="mt-4">
            <h2 className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              奖励与扣款
            </h2>
            <div className="rounded-2xl bg-white dark:bg-slate-800 border
                            border-slate-200 dark:border-slate-700 divide-y
                            divide-slate-100 dark:divide-slate-700/50">
              {others.map((i) => (
                <div key={i.id} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-slate-900 dark:text-slate-100">
                      {TYPE_LABEL[i.type] ?? i.type}
                    </div>
                    <div className="text-sm text-slate-500">
                      {i.note ?? '没有填写原因 —— 可以找调度问一下'}
                    </div>
                  </div>
                  <span
                    className={`tabular-nums font-medium shrink-0 ${
                      i.amount.startsWith('-')
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-green-700 dark:text-green-400'
                    }`}
                  >
                    {money(i.amount, detail.currency)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-4">
          <h2 className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
            送达明细({deliveries.length} 单)
          </h2>
          <div className="rounded-2xl bg-white dark:bg-slate-800 border
                          border-slate-200 dark:border-slate-700 divide-y
                          divide-slate-100 dark:divide-slate-700/50">
            {deliveries.map((i) => (
              <div key={i.id} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-slate-500">{i.order?.orderNo ?? '—'}</div>
                  <div className="text-slate-900 dark:text-slate-100 truncate">
                    {i.order?.receiverName ?? '—'}
                  </div>
                  {i.order?.deliveredAt && (
                    <div className="text-xs text-slate-400">
                      {i.order.deliveredAt.slice(5, 16).replace('T', ' ')}
                    </div>
                  )}
                </div>
                <span className="tabular-nums text-slate-700 dark:text-slate-300 shrink-0">
                  {money(i.amount, detail.currency)}
                </span>
              </div>
            ))}
          </div>
        </section>

        {detail.status === 'DRAFT' && (
          <p className="mt-4 text-sm text-slate-500">
            这一期还在核对中,金额可能还会变。确认之后就定下来了。
          </p>
        )}
        {detail.paidAt && (
          <p className="mt-4 text-sm text-slate-500">
            已于 {detail.paidAt.slice(0, 10)} 标记付款。没收到钱请找调度核对。
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span
        className={`tabular-nums ${
          danger ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
